import type { CompositeLayerConfig } from '../services/composite/tile-composite-service.js'
import type { GradientLegendConfig } from '../services/terrain/nwm-types.js'

const NDSI_GRADIENT_LEGEND: GradientLegendConfig = {
  stops: [
    { position: 0, color: 'rgba(240,240,128,0)' },
    { position: 0.01, color: '#F0F080' },
    { position: 0.2, color: '#F0D280' },
    { position: 0.4, color: '#F0B480' },
    { position: 0.6, color: '#F09680' },
    { position: 0.8, color: '#F07880' },
    { position: 1, color: '#FF0000' },
  ],
  ticks: [
    { position: 0, label: '0' },
    { position: 0.2, label: '20' },
    { position: 0.4, label: '40' },
    { position: 0.6, label: '60' },
    { position: 0.8, label: '80' },
    { position: 1, label: '100' },
  ],
}

export interface BaseLayer {
  id: string
  name: string
  url: string
  type?: 'raster' | 'vector' // default 'raster'
  options: {
    attribution: string
    maxZoom?: number
    subdomains?: string | string[]
    tileSize?: number
    zoomOffset?: number
    apikey?: string
  }
}

export type TemporalResolution = 'subdaily' | 'daily' | 'weekly' | 'monthly'

export type OverlayCategory = 'terrain' | 'imagery' | 'hazards' | 'land' | 'cellular' | 'comms'

export interface OverlayLayer {
  id: string
  name: string
  description?: string
  url: string
  category?: OverlayCategory
  type?: 'wmts' | 'wms' // Default to 'wmts' for backward compatibility
  wmsOptions?: {
    layers: string
    format?: string
    transparent?: boolean
    time?: string
  }
  legendUrl?: string // URL to color legend image
  gradientLegend?: import('../services/terrain/nwm-types.js').GradientLegendConfig
  classificationLegend?: Array<{ color: string; label: string }>
  timeAware?: boolean // Whether this layer supports TIME dimension
  temporalResolution?: TemporalResolution // How often data refreshes
  cmrCollection?: string // CMR short name for nearest-date lookup (sparse layers)
  latencyDays?: number // Processing latency — clamp requested date this many days back
  subdailyWmtsUrl?: string // WMTS tile URL template for subdaily mode (with {Time}, {z}, {y}, {x})
  subdaily?: boolean // Whether subdaily mode is currently active (runtime state)
  compositeConfig?: CompositeLayerConfig // If set, this layer supports time compositing
  options: {
    attribution: string
    maxZoom?: number
    maxNativeZoom?: number
    opacity?: number
    format?: string
    tileSize?: number
  }
  wmtsLayer?: import('../services/wmts/gibs-capabilities').WMTSLayer
}

export const baseLayers: BaseLayer[] = [
  {
    id: 'ofm-liberty',
    name: 'Liberty',
    type: 'vector',
    url: 'https://tiles.openfreemap.org/styles/liberty',
    options: {
      attribution:
        '© <a href="https://openfreemap.org">OpenFreeMap</a> · © <a href="https://www.openstreetmap.org/">OpenStreetMap</a>',
      maxZoom: 20,
    },
  },
  {
    id: 'ofm-bright',
    name: 'Bright',
    type: 'vector',
    url: 'https://tiles.openfreemap.org/styles/bright',
    options: {
      attribution:
        '© <a href="https://openfreemap.org">OpenFreeMap</a> · © <a href="https://www.openstreetmap.org/">OpenStreetMap</a>',
      maxZoom: 20,
    },
  },
  {
    id: 'ofm-positron',
    name: 'Positron',
    type: 'vector',
    url: 'https://tiles.openfreemap.org/styles/positron',
    options: {
      attribution:
        '© <a href="https://openfreemap.org">OpenFreeMap</a> · © <a href="https://www.openstreetmap.org/">OpenStreetMap</a>',
      maxZoom: 20,
    },
  },
  {
    id: 'ofm-dark',
    name: 'Dark Matter',
    type: 'vector',
    url: 'https://tiles.openfreemap.org/styles/dark',
    options: {
      attribution:
        '© <a href="https://openfreemap.org">OpenFreeMap</a> · © <a href="https://www.openstreetmap.org/">OpenStreetMap</a>',
      maxZoom: 20,
    },
  },
  {
    id: 'osm',
    name: 'OpenStreetMap',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    options: {
      attribution: '© <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors',
      maxZoom: 19,
    },
  },
  {
    id: 'opentopomap',
    name: 'OpenTopoMap',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    options: {
      attribution:
        'Map data: © <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: © <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
      maxZoom: 17,
    },
  },
  {
    id: 'usgs-topo',
    name: 'USGS Topographic',
    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}',
    options: {
      attribution: '© <a href="https://www.usgs.gov/">U.S. Geological Survey</a>',
      maxZoom: 16,
    },
  },
  {
    id: 'usgs-imagery-topo',
    name: 'USGS Satellite + Topo',
    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryTopo/MapServer/tile/{z}/{y}/{x}',
    options: {
      attribution: '© <a href="https://www.usgs.gov/">U.S. Geological Survey</a>',
      maxZoom: 16,
    },
  },
  {
    id: 'esri-satellite',
    name: 'Satellite Imagery',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    options: {
      attribution:
        'Tiles © Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
      maxZoom: 18,
    },
  },
]

/**
 * Built-in satellite overlay layers (always available, not dependent on GIBS capabilities)
 */
export function getSatelliteLayers(): OverlayLayer[] {
  // GIBS WMS only accepts YYYY-MM-DD format for TIME, even for GOES.
  // Use today — GIBS returns latest available imagery for the date.
  const today = new Date().toISOString().split('T')[0]

  return [
    // VIIRS — current operational polar-orbiting imagers (successor to MODIS)
    {
      id: 'viirs-snpp-truecolor',
      name: 'VIIRS SNPP True Color',
      description:
        'Daily true color imagery from Suomi-NPP polar orbiter (~1 day latency, 375m resolution). Enable composite for a cloud-free 7-day mosaic.',
      category: 'imagery',
      url: 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi',
      type: 'wms',
      wmsOptions: {
        layers: 'VIIRS_SNPP_CorrectedReflectance_TrueColor',
        format: 'image/jpeg',
        transparent: false,
        time: today,
      },
      timeAware: true,
      temporalResolution: 'daily',
      latencyDays: 1,
      compositeConfig: {
        id: 'viirs-snpp-truecolor',
        wmsLayer: 'VIIRS_SNPP_CorrectedReflectance_TrueColor',
        format: 'image/png',
        windowDays: 7,
        latencyDays: 1,
        maxNativeZoom: 9,
        cloudMaskLayer: 'VIIRS_SNPP_Cloud_Optical_Thickness',
        attribution: '© NASA GIBS VIIRS (cloud-filtered composite)',
      },
      options: {
        attribution: '© NASA GIBS VIIRS',
        maxZoom: 19,
        maxNativeZoom: 9,
        opacity: 0.8,
      },
    },
    {
      id: 'viirs-noaa20-truecolor',
      name: 'VIIRS NOAA-20 True Color',
      description:
        'Daily true color imagery from NOAA-20 polar orbiter (~1 day latency, 375m resolution). Second daily pass offsets SNPP timing.',
      category: 'imagery',
      url: 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi',
      type: 'wms',
      wmsOptions: {
        layers: 'VIIRS_NOAA20_CorrectedReflectance_TrueColor',
        format: 'image/jpeg',
        transparent: false,
        time: today,
      },
      timeAware: true,
      temporalResolution: 'daily',
      latencyDays: 1,
      options: {
        attribution: '© NASA GIBS VIIRS',
        maxZoom: 19,
        maxNativeZoom: 9,
        opacity: 0.8,
      },
    },
    {
      id: 'viirs-snpp-snow-wms',
      name: 'SNPP',
      description:
        'Satellite-derived snow cover from Suomi-NPP using Normalized Difference Snow Index. Shows snow extent, not depth. Cloud-obstructed areas may have gaps.',
      category: 'hazards',
      url: 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi',
      type: 'wms',
      wmsOptions: {
        layers: 'VIIRS_SNPP_NDSI_Snow_Cover',
        format: 'image/png',
        transparent: true,
        time: today,
      },
      gradientLegend: NDSI_GRADIENT_LEGEND,
      timeAware: true,
      temporalResolution: 'daily',
      latencyDays: 1,
      options: {
        attribution: '© NASA GIBS VIIRS SNPP',
        maxZoom: 19,
        maxNativeZoom: 13,
        opacity: 0.7,
      },
    },
    {
      id: 'viirs-noaa20-snow-wms',
      name: 'NOAA-20',
      description:
        'Satellite-derived snow cover from NOAA-20 using Normalized Difference Snow Index. Second daily pass provides additional cloud-gap coverage.',
      category: 'hazards',
      url: 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi',
      type: 'wms',
      wmsOptions: {
        layers: 'VIIRS_NOAA20_NDSI_Snow_Cover',
        format: 'image/png',
        transparent: true,
        time: today,
      },
      gradientLegend: NDSI_GRADIENT_LEGEND,
      timeAware: true,
      temporalResolution: 'daily',
      latencyDays: 1,
      options: {
        attribution: '© NASA GIBS VIIRS NOAA-20',
        maxZoom: 19,
        maxNativeZoom: 13,
        opacity: 0.7,
      },
    },
    // GOES — geostationary, sub-daily (10-min intervals, 90-day rolling window)
    {
      id: 'goes-east-truecolor',
      name: 'GOES-East GeoColor',
      description:
        'Geostationary imagery updated every 10 minutes covering the Americas. Low resolution (2km) but near real-time. 90-day rolling archive.',
      category: 'imagery',
      url: 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi',
      type: 'wms',
      wmsOptions: {
        layers: 'GOES-East_ABI_GeoColor',
        format: 'image/jpeg',
        transparent: false,
        time: today,
      },
      timeAware: true,
      temporalResolution: 'subdaily',
      subdailyWmtsUrl:
        'https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/GOES-East_ABI_GeoColor/default/{Time}/GoogleMapsCompatible_Level7/{z}/{y}/{x}.png',
      options: {
        attribution: '© NASA GIBS GOES-East',
        maxZoom: 19,
        maxNativeZoom: 7,
        opacity: 0.8,
      },
    },
    {
      id: 'goes-west-truecolor',
      name: 'GOES-West GeoColor',
      description:
        'Geostationary imagery updated every 10 minutes covering the Pacific and western Americas. Low resolution (2km) but near real-time. 90-day rolling archive.',
      category: 'imagery',
      url: 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi',
      type: 'wms',
      wmsOptions: {
        layers: 'GOES-West_ABI_GeoColor',
        format: 'image/jpeg',
        transparent: false,
        time: today,
      },
      timeAware: true,
      temporalResolution: 'subdaily',
      subdailyWmtsUrl:
        'https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/GOES-West_ABI_GeoColor/default/{Time}/GoogleMapsCompatible_Level7/{z}/{y}/{x}.png',
      options: {
        attribution: '© NASA GIBS GOES-West',
        maxZoom: 19,
        maxNativeZoom: 7,
        opacity: 0.8,
      },
    },
    // Sentinel-2 / Landsat (HLS) — high-res but sparse coverage (5-16 day revisit)
    {
      id: 'hls-sentinel-truecolor',
      name: 'Sentinel-2 True Color (HLS)',
      description:
        'High-resolution (30m) true color from ESA Sentinel-2. ~5 day revisit means sparse single-day coverage — enable composite for a cloud-free 12-day mosaic.',
      category: 'imagery',
      url: 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi',
      type: 'wms',
      wmsOptions: {
        layers: 'HLS_S30_Nadir_BRDF_Adjusted_Reflectance',
        format: 'image/png',
        transparent: true,
        time: today,
      },
      timeAware: true,
      temporalResolution: 'weekly',
      cmrCollection: 'HLSS30',
      compositeConfig: {
        id: 'hls-sentinel-truecolor',
        wmsLayer: 'HLS_S30_Nadir_BRDF_Adjusted_Reflectance',
        windowDays: 12,
        latencyDays: 1,
        maxNativeZoom: 14,
        attribution: '© NASA GIBS HLS Sentinel-2',
      },
      options: {
        attribution: '© NASA GIBS HLS Sentinel-2',
        maxZoom: 19,
        maxNativeZoom: 14,
        opacity: 0.8,
      },
    },
    {
      id: 'hls-landsat-truecolor',
      name: 'Landsat True Color (HLS)',
      description:
        'High-resolution (30m) true color from Landsat 8/9. ~16 day revisit — enable composite for full coverage. Complements Sentinel-2 for combined ~3 day revisit.',
      category: 'imagery',
      url: 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi',
      type: 'wms',
      wmsOptions: {
        layers: 'HLS_L30_Nadir_BRDF_Adjusted_Reflectance',
        format: 'image/png',
        transparent: true,
        time: today,
      },
      timeAware: true,
      temporalResolution: 'weekly',
      cmrCollection: 'HLSL30',
      compositeConfig: {
        id: 'hls-landsat-truecolor',
        wmsLayer: 'HLS_L30_Nadir_BRDF_Adjusted_Reflectance',
        windowDays: 16,
        latencyDays: 1,
        maxNativeZoom: 14,
        attribution: '© NASA GIBS HLS Landsat',
      },
      options: {
        attribution: '© NASA GIBS HLS Landsat',
        maxZoom: 19,
        maxNativeZoom: 14,
        opacity: 0.8,
      },
    },
    // VIIRS NOAA-21 — third polar-orbiting pass for additional daily coverage
    {
      id: 'viirs-noaa21-truecolor',
      name: 'VIIRS NOAA-21 True Color',
      description:
        'Daily true color imagery from NOAA-21 polar orbiter (~1 day latency, 375m resolution). Third daily VIIRS pass for maximum temporal coverage.',
      category: 'imagery',
      url: 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi',
      type: 'wms',
      wmsOptions: {
        layers: 'VIIRS_NOAA21_CorrectedReflectance_TrueColor',
        format: 'image/jpeg',
        transparent: false,
        time: today,
      },
      timeAware: true,
      temporalResolution: 'daily',
      latencyDays: 1,
      options: {
        attribution: '© NASA GIBS VIIRS',
        maxZoom: 19,
        maxNativeZoom: 9,
        opacity: 0.8,
      },
    },
    // Vegetation disturbance alerts — burn scars, blowdowns, landslides
    {
      id: 'vegetation-disturbance',
      name: 'Vegetation Disturbance Alerts',
      description:
        'Detects burn scars, blowdowns, landslides, and other vegetation loss from HLS satellite imagery. Updated daily, 30m resolution. Enable composite to fill coverage gaps from satellite revisit cycles.',
      category: 'hazards',
      url: 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi',
      type: 'wms',
      wmsOptions: {
        layers: 'OPERA_L3_DIST-ALERT-HLS_Color_Index',
        format: 'image/png',
        transparent: true,
        time: today,
      },
      timeAware: true,
      temporalResolution: 'daily',
      cmrCollection: 'OPERA_L3_DIST-ALERT-HLS_V1',
      compositeConfig: {
        id: 'vegetation-disturbance',
        wmsLayer: 'OPERA_L3_DIST-ALERT-HLS_Color_Index',
        windowDays: 5,
        maxNativeZoom: 12,
        attribution: '© NASA GIBS OPERA HLS (composite)',
        colorKeys: [{ r: 18, g: 18, b: 18, tolerance: 3 }],
      },
      classificationLegend: [
        { color: '#005555', label: 'First detection < 50%' },
        { color: '#897F4E', label: 'Provisional < 50%' },
        { color: '#DEE043', label: 'Confirmed < 50%' },
        { color: '#008888', label: 'First detection ≥ 50%' },
        { color: '#E48727', label: 'Provisional ≥ 50%' },
        { color: '#E01B07', label: 'Confirmed ≥ 50%' },
        { color: '#777777', label: 'Finished < 50%' },
        { color: '#DDDDDD', label: 'Finished ≥ 50%' },
      ],
      options: {
        attribution: '© NASA GIBS OPERA HLS',
        maxZoom: 19,
        maxNativeZoom: 12,
        opacity: 0.7,
      },
    },
  ]
}
